import React, { useId, useRef } from 'react';
import { Facebook, Instagram, Twitter, Linkedin } from 'lucide-react';
import EmojiPickerButton from './EmojiPickerButton';
import { useToast } from '../../shared/ToastProvider';
import '../TemplateManagement.css';

const FooterSection = ({ formData, errors, onChange }) => {
  const signatureRef = useRef(null);
  const sloganRef = useRef(null);
  const toast = useToast();

  const updateFooter = (field, value) => {
    onChange({ [field]: value });
  };

  const handleSignatureEmoji = (emoji) => {
    const el = signatureRef.current;
    const currentVal = formData.footerSignature || '';
    if (el) {
      const cursorPos = el.selectionStart ?? currentVal.length;
      const before = currentVal.substring(0, cursorPos);
      const after = currentVal.substring(cursorPos);
      updateFooter('footerSignature', before + emoji + after);
      requestAnimationFrame(() => {
        el.focus();
        const newPos = cursorPos + emoji.length;
        el.setSelectionRange(newPos, newPos);
      });
    } else {
      updateFooter('footerSignature', currentVal + emoji);
    }
  };

  const handleSloganEmoji = (emoji) => {
    const input = sloganRef.current;
    const currentVal = formData.footerSlogan || '';
    if (input) {
      const cursorPos = input.selectionStart ?? currentVal.length;
      const before = currentVal.substring(0, cursorPos);
      const after = currentVal.substring(cursorPos);
      updateFooter('footerSlogan', before + emoji + after);
      requestAnimationFrame(() => {
        input.focus();
        const newPos = cursorPos + emoji.length;
        input.setSelectionRange(newPos, newPos);
      });
    } else {
      updateFooter('footerSlogan', currentVal + emoji);
    }
  };

  const fileToDataUrl = (file) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.readAsDataURL(file);
    });

  const handleImageUpload = async (event, field) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 4 * 1024 * 1024) {
      toast.error?.('File size must not exceed 4MB');
      return;
    }

    const dataUrl = await fileToDataUrl(file);
    onChange({
      [field + 'UrlPreview']: dataUrl,
      [field + 'UrlFile']: file
    });
  };

  const handleRemoveImage = (field) => {
    onChange({
      [field + 'UrlPreview']: '',
      [field + 'UrlFile']: null
    });
  };

  const updateSocialLink = (platform, url) => {
    onChange({
      socialLinks: {
        ...formData.socialLinks,
        [platform]: url
      }
    });
  };

  const footerImageInputId = useId();

  const renderImageUploader = ({ field, label, inputId, guidelines = [] }) => {
    const url = formData[field + 'UrlPreview'];
    const hasImage = Boolean(url);

    return (
      <div className="emtpl-upload-card">
        <div className="emtpl-upload-row">
          <label
            className={`emtpl-upload-dropzone${hasImage ? ' has-image' : ''}`}
            htmlFor={inputId}
          >
            <input
              id={inputId}
              type="file"
              accept="image/*"
              onChange={(e) => handleImageUpload(e, field)}
            />
            {hasImage ? (
              <div className="emtpl-upload-preview">
                <img src={url} alt={`${label} preview`} />
                <button
                  type="button"
                  className="emtpl-upload-remove"
                  aria-label={`Remove ${label}`}
                  title="Remove"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRemoveImage(field);
                  }}
                >
                  &times;
                </button>
              </div>
            ) : (
              <div className="emtpl-upload-placeholder">
                <div className="emtpl-upload-icon">🖼️</div>
                <div>
                  <div className="emtpl-upload-title">{label}</div>
                  <div className="emtpl-upload-cta">Click to upload</div>
                </div>
              </div>
            )}
          </label>

          {!hasImage && guidelines.length > 0 && (
            <div className="emtpl-upload-guidelines">
              {guidelines.map((text, i) => (
                <span key={text + i}>• {text}</span>
              ))}
            </div>
          )}

          {hasImage && (
            <div className="emtpl-upload-actions">
              <label htmlFor={inputId} className="em-btn em-btn-secondary em-btn-sm">
                Change
              </label>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="emtpl-section emtpl-footer-section">
      {/* Logo Upload */}
      <div className="emtpl-footer-card">
        <label className="emtpl-footer-label">Footer Logo</label>
        {renderImageUploader({
          field: 'footerImage',
          label: 'Upload Footer Logo',
          inputId: footerImageInputId,
          guidelines: ['Recommended: square PNG/SVG', 'Max size: 4MB']
        })}
      </div>

      {/* Signature */}
      <div className="emtpl-footer-card">
        <div className="emtpl-footer-card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label className="emtpl-footer-label" style={{ marginBottom: 0 }}>Signature</label>
            <EmojiPickerButton onSelect={handleSignatureEmoji} />
          </div>
          <label className="emtpl-checkbox">
            <input
              type="checkbox"
              checked={formData.footerShowUnsubscribe}
              onChange={(e) => updateFooter('footerShowUnsubscribe', e.target.checked)}
            />
            <span>Show Unsubscribe Link</span>
          </label>
        </div>
        <textarea
          ref={signatureRef}
          className="em-textarea"
          rows={3}
          value={formData.footerSignature}
          onChange={(e) => updateFooter('footerSignature', e.target.value)}
          placeholder={"With Best Wishes,\nTeam MessageOS"}
        />
        <p className="em-text-xs em-text-muted" style={{ marginTop: 6 }}>
          Unsubscribe links are auto-generated per recipient. No manual URL needed.
        </p>
      </div>

      {/* Company Info */}
      <div className="emtpl-footer-card">
        <label className="emtpl-footer-label">Company Information</label>
        <div className="emtpl-footer-grid">
          <div className="emtpl-group">
            <label className="em-label">Website URL</label>
            <input
              type="url"
              className="em-input"
              value={formData.footerWebsiteUrl}
              onChange={(e) => updateFooter('footerWebsiteUrl', e.target.value)}
              placeholder="https://www.example.com"
            />
          </div>
          <div className="emtpl-group">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label className="em-label" style={{ marginBottom: 0 }}>Slogan</label>
              <EmojiPickerButton onSelect={handleSloganEmoji} />
            </div>
            <input
              ref={sloganRef}
              type="text"
              className="em-input"
              value={formData.footerSlogan}
              onChange={(e) => updateFooter('footerSlogan', e.target.value)}
              placeholder="Your company tagline"
            />
          </div>
          <div className="emtpl-group emtpl-footer-full">
            <label className="em-label">Company Address</label>
            <input
              type="text"
              className="em-input"
              value={formData.footerCompanyAddress}
              onChange={(e) => updateFooter('footerCompanyAddress', e.target.value)}
              placeholder="Company Address, City, Country"
            />
          </div>
        </div>
      </div>

      {/* Social Links */}
      <div className="emtpl-footer-card">
        <label className="emtpl-footer-label">Social Media Links</label>
        <div className="emtpl-social-grid">
          <div className="emtpl-group">
            <label className="emtpl-social-label"><Facebook size={16} /> Facebook</label>
            <input
              type="url"
              className="em-input"
              value={formData.socialLinks?.facebook || ''}
              onChange={(e) => updateSocialLink('facebook', e.target.value)}
              placeholder="Facebook URL"
            />
          </div>
          <div className="emtpl-group">
            <label className="emtpl-social-label"><Instagram size={16} /> Instagram</label>
            <input
              type="url"
              className="em-input"
              value={formData.socialLinks?.instagram || ''}
              onChange={(e) => updateSocialLink('instagram', e.target.value)}
              placeholder="Instagram URL"
            />
          </div>
          <div className="emtpl-group">
            <label className="emtpl-social-label"><Twitter size={16} /> Twitter</label>
            <input
              type="url"
              className="em-input"
              value={formData.socialLinks?.twitter || ''}
              onChange={(e) => updateSocialLink('twitter', e.target.value)}
              placeholder="Twitter URL"
            />
          </div>
          <div className="emtpl-group">
            <label className="emtpl-social-label"><Linkedin size={16} /> LinkedIn</label>
            <input
              type="url"
              className="em-input"
              value={formData.socialLinks?.linkedin || ''}
              onChange={(e) => updateSocialLink('linkedin', e.target.value)}
              placeholder="LinkedIn URL"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FooterSection;
